import React, { useState, useEffect } from 'react';
import api from '../services/api';

interface Permission {
  id: number;
  name: string;
  codename: string;
  content_type: number;
}

interface Group {
  id?: number;
  name: string;
  permissions: number[];
}

interface ProfileFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  profileToEdit?: Group | null;
}

const ProfileForm: React.FC<ProfileFormProps> = ({ onSuccess, onCancel, profileToEdit }) => {
  const [name, setName] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  const [groupedPermissions, setGroupedPermissions] = useState<{[key: string]: Permission[]}>({});

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await api.get('permissions/');
        
        const grouped: {[key: string]: Permission[]} = {};
        response.data.forEach((perm: Permission) => {
          // Extract model name (e.g., "Can add meeting" -> "Meeting")
          // Heuristic: split by space, take last word or infer from codename
          let modelName = perm.codename.split('_')[1]; 
          if (!modelName) modelName = "Outros";
          
          // Translate model names for better UX
          const translations: {[key: string]: string} = {
            'meeting': 'Reuniões',
            'event': 'Eventos',
            'project': 'Projetos',
            'survey': 'Pesquisas',
            'biblestudy': 'Estudos Bíblicos',
            'incident': 'Ocorrências',
            'content': 'Conteúdos',
            'parent': 'Pais',
            'schoolclass': 'Turmas',
            'staff': 'Colaboradores',
            'user': 'Usuários',
            'group': 'Perfis',
            'kpitarget': 'Configurações (KPIs)',
            'meetingattendance': 'Presença Reunião',
            'eventattendance': 'Presença Evento',
            'eventvolunteer': 'Voluntários',
            'surveyresponse': 'Respostas Pesquisa'
          };

          const groupName = translations[modelName] || modelName.charAt(0).toUpperCase() + modelName.slice(1);
          
          if (!grouped[groupName]) {
            grouped[groupName] = [];
          }
          grouped[groupName].push(perm);
        });
        setGroupedPermissions(grouped);

      } catch (error) {
        console.error('Failed to fetch permissions', error);
      }
    };
    fetchPermissions();
  }, []);

  useEffect(() => {
    if (profileToEdit) {
      setName(profileToEdit.name);
      setSelectedPermissions(profileToEdit.permissions);
    } else {
      setName('');
      setSelectedPermissions([]);
    }
  }, [profileToEdit]);

  const handlePermissionToggle = (permId: number) => {
    setSelectedPermissions(prev => 
      prev.includes(permId) ? prev.filter(id => id !== permId) : [...prev, permId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const profileData = { name, permissions: selectedPermissions };

    try {
      if (profileToEdit && profileToEdit.id) {
        await api.patch(`groups/${profileToEdit.id}/`, profileData);
      } else {
        await api.post('groups/', profileData);
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to save profile', error);
      alert('Erro ao salvar perfil.');
    }
  };

  const isViewPermission = (codename: string) => codename.startsWith('view_');

  return (
    <div className="card">
      <h3>{profileToEdit ? 'Editar Perfil' : 'Novo Perfil'}</h3>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Nome do Perfil</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>

        <div className="form-group">
          <label>Permissões de Acesso</label>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '10px' }}>
            Marque "Visualizar" (view) para dar acesso à tela correspondente no menu.
          </p>
          <div style={{ maxHeight: '500px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '15px' }}>
            {Object.keys(groupedPermissions).sort().map(groupName => (
              <div key={groupName} style={{ marginBottom: '20px' }}>
                <h4 style={{ fontSize: '1rem', color: 'var(--primary-blue)', borderBottom: '1px solid #eee', paddingBottom: '5px', marginBottom: '10px' }}>
                  {groupName}
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
                  {groupedPermissions[groupName].map(perm => (
                    <div key={perm.id} style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      backgroundColor: isViewPermission(perm.codename) ? '#f0f9ff' : 'transparent',
                      padding: '5px',
                      borderRadius: '4px'
                    }}>
                      <input 
                        type="checkbox" 
                        id={`perm-${perm.id}`} 
                        checked={selectedPermissions.includes(perm.id)}
                        onChange={() => handlePermissionToggle(perm.id)}
                        style={{ width: 'auto', marginRight: '8px' }}
                      />
                      <label htmlFor={`perm-${perm.id}`} style={{ margin: 0, fontSize: '0.85rem', cursor: 'pointer', fontWeight: isViewPermission(perm.codename) ? '600' : '400' }}>
                        {perm.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button type="button" onClick={onCancel} className="btn btn-secondary">Cancelar</button>
          <button type="submit" className="btn">Salvar</button>
        </div>
      </form>
    </div>
  );
};

export default ProfileForm;
